/**
 * Zachycení nativní instalační výzvy PWA (Chrome/Edge/Android).
 * Událost `beforeinstallprompt` přijde jen jednou a klidně dřív, než se
 * připojí komponenta s návodem — proto ji chytáme hned při startu (main.tsx)
 * a držíme tady. iOS Safari tuhle událost nemá vůbec.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((fn) => fn());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
}

export function canPromptInstall(): boolean {
  return deferred !== null;
}

export function subscribeInstallPrompt(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Otevře nativní dialog. Vrací true, když uživatel instalaci potvrdil. */
export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false;
  const ev = deferred;
  deferred = null;
  notify();
  await ev.prompt();
  const { outcome } = await ev.userChoice;
  return outcome === 'accepted';
}

/** Běží už jako nainstalovaná aplikace (standalone okno)? */
export function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export type InstallPlatform = 'ios' | 'android' | 'desktop';

export function detectPlatform(): InstallPlatform {
  const ua = navigator.userAgent;
  // iPadOS 13+ se hlásí jako Mac — rozliší ho až dotyk.
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}
